import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Play, Pause, Lock, CircleNotch } from 'phosphor-react';

const BAR_COUNT = 52;
const MIN_BAR_HEIGHT = 0.06;
// Speed multiplier for waveform animation
const WAVE_SPEED = 2.8;

function generateFallbackBars(count) {
  const bars = [];
  for (let i = 0; i < count; i++) {
    const t = i / count;
    const height = MIN_BAR_HEIGHT + (1 - MIN_BAR_HEIGHT) * (Math.sin(t * Math.PI * 4) * 0.5 + 0.5);
    bars.push(height);
  }
  return bars;
}

async function extractWaveformFromAudio(audioUrl, barCount, abortSignal) {
  try {
    const response = await fetch(audioUrl, { signal: abortSignal });
    if (!response.ok) return null;
    const arrayBuffer = await response.arrayBuffer();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    audioContext.close();

    const channelData = audioBuffer.getChannelData(0);
    const samplesPerBar = Math.floor(channelData.length / barCount);
    const bars = [];

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      const end = Math.min(start + samplesPerBar, channelData.length);
      let max = 0;
      for (let j = start; j < end; j++) {
        const val = Math.abs(channelData[j]);
        if (val > max) max = val;
      }
      bars.push(max);
    }

    const maxVal = Math.max(...bars, 0.0001);
    const normalized = bars.map((b) => Math.max(MIN_BAR_HEIGHT, b / maxVal));
    return normalized;
  } catch {
    return null;
  }
}

function formatTime(seconds) {
  if (seconds === Infinity || isNaN(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function isValidMediaDuration(seconds) {
  return (
    typeof seconds === 'number' &&
    Number.isFinite(seconds) &&
    seconds > 0 &&
    seconds !== Infinity
  );
}

/**
 * Dynamic frequency animation — all bars move through the full range.
 * t is already pre-multiplied by WAVE_SPEED.
 */
function animateFallback(staticBars, t) {
  const n = staticBars.length;
  return staticBars.map((_, i) => {
    const norm = i / n;
    // Traveling wave left→right
    const sweep1 = Math.sin(t * 6.1  - norm * Math.PI * 5.3 + 0.4) * 0.38;
    // Counter-traveling wave right→left
    const sweep2 = Math.sin(t * 4.3  + norm * Math.PI * 3.7 + 1.8) * 0.24;
    // Fast per-bar flutter
    const local1 = Math.sin(t * (7.7  + norm * 3.5) + i * 1.31 + 0.6) * 0.24;
    const local2 = Math.cos(t * (5.9  - norm * 2.1) + i * 0.93 + 2.3) * 0.16;

    const combined = sweep1 + sweep2 + local1 + local2;
    const wave = Math.max(0, Math.min(1, (combined + 1.02) / 2.04));
    return 0.08 + 0.87 * wave;
  });
}

/**
 * Dynamic waveform animation based on decoded audio envelope.
 * Uses the static waveform as shape influence while producing
 * full-range frequency-visualizer-style bar motion.
 */
function animateWaveform(staticBars, t) {
  const n = staticBars.length;
  return staticBars.map((base, i) => {
    const norm = i / n;
    const sweep1 = Math.sin(t * 5.4 - norm * Math.PI * 4.6 + 0.3) * 0.36;
    const sweep2 = Math.sin(t * 3.9 + norm * Math.PI * 3.2 + 1.7) * 0.26;
    const local1 = Math.sin(t * (7.2 + norm * 3.0) + i * 1.15 + 0.8) * 0.22;
    const local2 = Math.cos(t * (5.6 - norm * 2.4) + i * 0.87 + 2.0) * 0.14;

    const combined = sweep1 + sweep2 + local1 + local2;
    const wave = Math.max(0, Math.min(1, (combined + 0.98) / 1.96));
    const dynamic = 0.10 + 0.85 * wave;
    return Math.max(MIN_BAR_HEIGHT, Math.min(1, dynamic * 0.65 + base * 0.35));
  });
}

function QuestionAudioPlayer({
  audioUrl,
  onEnded,
  compact = false,
  autoPlay = false,
  onPlayStarted,
  showQuota = true,
  maxPlaysMode = 5,
  fullWidth = false,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isWaveformLoading, setIsWaveformLoading] = useState(false);
  const [isPreparingVisualizer, setIsPreparingVisualizer] = useState(false);
  const [error, setError] = useState(null);
  const [waveformBars, setWaveformBars] = useState(null);
  const [animBars, setAnimBars] = useState(null);
  const [playCount, setPlayCount] = useState(0);

  // Quota strategy:
  // - 5 (default): fixed 5 plays for practice
  // - 'unlimited': always unlimited
  // - number: fixed quota
  const maxPlays = useMemo(() => {
    if (!audioUrl) return null;
    if (maxPlaysMode === 'unlimited') return null;
    if (typeof maxPlaysMode === 'number' && Number.isFinite(maxPlaysMode)) {
      return Math.max(0, Math.floor(maxPlaysMode));
    }
    return null;
  }, [audioUrl, maxPlaysMode]);

  const hasValidDuration = isValidMediaDuration(duration);
  /** Media duration not ready yet (common for blob URLs on iOS until decode/play). */
  const isDurationPending = Boolean(audioUrl && !error && !hasValidDuration);

  // MediaElementSource can become silent on cross-origin audio without CORS headers.
  // Use Web Audio graph only for same-origin URLs; otherwise play directly from HTMLAudioElement.
  const shouldUseWebAudioGraph = useMemo(() => {
    if (!audioUrl || typeof window === 'undefined') return false;
    try {
      const resolved = new URL(audioUrl, window.location.href);
      return resolved.origin === window.location.origin;
    } catch {
      return false;
    }
  }, [audioUrl]);

  const isQuotaExhausted = showQuota && maxPlays !== null && playCount >= maxPlays;
  const remainingPlays = maxPlays !== null ? Math.max(0, maxPlays - playCount) : null;

  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const freqDataRef = useRef(null);
  const waveformRef = useRef(null);

  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const updateProgress = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      const d = audioRef.current.duration;
      if (isValidMediaDuration(d)) {
        setDuration(d);
      }
    }
  }, []);

  useEffect(() => {
    waveformRef.current = waveformBars;
  }, [waveformBars]);

  const setupAudioGraph = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return false;

    if (sourceNodeRef.current) {
      try {
        if (audioCtxRef.current?.state === 'suspended') {
          await audioCtxRef.current.resume();
        }
      } catch {
        // no-op
      }
      return true;
    }

    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.72;
      analyser.minDecibels = -75;
      analyser.maxDecibels = -10;

      // Prefer MediaElementSource (more stable on first playback).
      // Fallback to captureStream for browsers/source URLs that block MediaElementSource.
      let source = null;
      try {
        source = ctx.createMediaElementSource(audio);
        source.connect(analyser);
        analyser.connect(ctx.destination);
      } catch {
        const stream = audio.captureStream?.() || audio.mozCaptureStream?.();
        if (!stream) {
          ctx.close();
          return false;
        }
        source = ctx.createMediaStreamSource(stream);
        source.connect(analyser);
      }

      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceNodeRef.current = source;
      // Time-domain data gives smoother full-width bar motion than raw frequency bins.
      freqDataRef.current = new Uint8Array(analyser.fftSize);
      return true;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('QuestionAudioPlayer: Web Audio setup failed', err);
      }
      return false;
    }
  }, []);

  const stopAnimLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setAnimBars(null);
  }, []);

  const startAnimLoop = useCallback(() => {
    const run = () => {
      const t = (performance.now() / 1000) * WAVE_SPEED;
      const staticBars = waveformRef.current || generateFallbackBars(BAR_COUNT);
      const hasDecodedWaveform = Array.isArray(waveformRef.current) && waveformRef.current.length === BAR_COUNT;

      const bars = hasDecodedWaveform
        ? animateWaveform(staticBars, t)
        : animateFallback(staticBars, t);

      setAnimBars(bars);
      rafRef.current = requestAnimationFrame(run);
    };
    rafRef.current = requestAnimationFrame(run);
  }, []);

  useEffect(() => {
    if (!audioUrl) {
      setWaveformBars(null);
      setIsWaveformLoading(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      setAnimBars(null);
      setPlayCount(0);
      clearProgressInterval();
      stopAnimLoop();
      return;
    }

    const abortController = new AbortController();
    setIsWaveformLoading(true);
    let shouldFetchWaveform = false;
    try {
      const resolved = new URL(audioUrl, window.location.href);
      shouldFetchWaveform = resolved.origin === window.location.origin;
    } catch {
      shouldFetchWaveform = false;
    }
    if (!shouldFetchWaveform) {
      setWaveformBars(generateFallbackBars(BAR_COUNT));
      setIsWaveformLoading(false);
      return () => {
        abortController.abort();
      };
    }
    extractWaveformFromAudio(audioUrl, BAR_COUNT, abortController.signal)
      .then((bars) => {
        if (!abortController.signal.aborted) {
          setWaveformBars(bars || generateFallbackBars(BAR_COUNT));
        }
      })
      .catch(() => {
        if (!abortController.signal.aborted) {
          setWaveformBars(generateFallbackBars(BAR_COUNT));
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsWaveformLoading(false);
        }
      });

    return () => {
      abortController.abort();
      setIsWaveformLoading(false);
    };
  }, [audioUrl, stopAnimLoop]);

  useEffect(() => {
    if (!audioUrl) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setError(null);
      setPlayCount(0);
      clearProgressInterval();
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
    }

    const audio = audioRef.current;
    setError(null);
    setIsLoading(true);

    const syncDurationFromElement = () => {
      const d = audio.duration;
      if (isValidMediaDuration(d)) {
        setDuration(d);
      }
    };

    const handleLoadedMetadata = () => {
      syncDurationFromElement();
      setIsLoading(false);
    };

    const handleDurationChange = () => {
      syncDurationFromElement();
    };

    const handleCanPlay = () => {
      syncDurationFromElement();
      setIsLoading(false);
      if (autoPlay) {
        const shouldSetupGraph = shouldUseWebAudioGraph && !sourceNodeRef.current;
        const playAudio = () => {
          audio.play().then(() => {
            setIsPlaying(true);
            onPlayStarted?.();
          }).catch(() => {});
        };
        if (shouldSetupGraph) {
          setupAudioGraph().finally(playAudio);
        } else {
          playAudio();
        }
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (maxPlays !== null) {
        setPlayCount((c) => c + 1);
      }
      clearProgressInterval();
      stopAnimLoop();
      onEnded?.();
    };

    const handleError = (e) => {
      setIsPlaying(false);
      setIsLoading(false);
      clearProgressInterval();
      stopAnimLoop();
      setError('Failed to load audio');
      if (process.env.NODE_ENV === 'development') {
        console.warn('QuestionAudioPlayer: Audio load error', e);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    audio.preload = 'metadata';
    audio.src = audioUrl;
    audio.load();

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
      clearProgressInterval();
      stopAnimLoop();
    };
  }, [audioUrl, onEnded, maxPlays, autoPlay, onPlayStarted, shouldUseWebAudioGraph, clearProgressInterval, setupAudioGraph, stopAnimLoop]);

  useEffect(() => {
    if (isPlaying && audioRef.current) {
      progressIntervalRef.current = setInterval(updateProgress, 100);
    }
    return () => clearProgressInterval();
  }, [isPlaying, updateProgress, clearProgressInterval]);

  useEffect(() => {
    if (isPlaying) {
      startAnimLoop();
    } else {
      stopAnimLoop();
    }
    return () => stopAnimLoop();
  }, [isPlaying, startAnimLoop, stopAnimLoop]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      audioCtxRef.current?.close?.();
      sourceNodeRef.current = null;
      analyserRef.current = null;
      audioCtxRef.current = null;
      clearProgressInterval();
      stopAnimLoop();
    };
  }, [clearProgressInterval, stopAnimLoop]);

  const togglePlayPause = async () => {
    if (!audioRef.current || !audioUrl || error) return;
    if (isQuotaExhausted) return;

    if (isPlaying) {
      audioRef.current.pause();
      clearProgressInterval();
      setIsPlaying(false);
      return;
    }

    try {
      const shouldSetupGraph = shouldUseWebAudioGraph && !sourceNodeRef.current;
      if (shouldSetupGraph) {
        setIsPreparingVisualizer(true);
        await setupAudioGraph();
        // Give analyser 1-2 frames to stabilize first playback visualization.
        await new Promise((resolve) => setTimeout(resolve, 80));
        setIsPreparingVisualizer(false);
      } else {
        setIsPreparingVisualizer(false);
      }
      await audioRef.current.play();
      setIsPlaying(true);
      onPlayStarted?.();
    } catch (err) {
      setIsPreparingVisualizer(false);
      if (process.env.NODE_ENV === 'development') {
        console.warn('QuestionAudioPlayer: Play failed', err);
      }
      setError('Playback failed');
    }
  };

  const handleWaveformClick = (e) => {
    if (!audioRef.current || !hasValidDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  if (!audioUrl) return null;

  const idleBars = waveformBars || generateFallbackBars(BAR_COUNT);
  const displayBars = isPlaying && animBars ? animBars : null;
  const progress = hasValidDuration ? currentTime / duration : 0;

  const playsUsed = maxPlays !== null ? maxPlays - (remainingPlays ?? 0) : 0;
  const isPlayerLoading = isLoading || isWaveformLoading || isPreparingVisualizer;

  const rootCls = [
    'question-audio-player w-full py-3 px-4 bg-transparent border-none rounded-[10px] mb-0',
    compact ? 'py-2.5 px-3.5' : '',
    isQuotaExhausted ? 'opacity-75' : '',
  ].filter(Boolean).join(' ');

  const quotaBannerCls = [
    'inline-flex items-center justify-start gap-2.5 mb-2.5 py-1.5 px-2.5 rounded-lg text-xs border w-fit',
    isQuotaExhausted
      ? 'bg-gray-500/10 border-gray-500/25 text-gray-400'
      : maxPlays === null
        ? 'bg-[rgba(18,118,109,0.06)] dark:bg-[rgba(20,184,166,0.05)] border-[rgba(18,118,109,0.14)] dark:border-[rgba(20,184,166,0.15)] text-[var(--accent-teal)]'
        : 'bg-[rgba(18,118,109,0.08)] dark:bg-[rgba(20,184,166,0.08)] border-[rgba(18,118,109,0.2)] dark:border-[rgba(20,184,166,0.22)] text-[var(--accent-teal)]',
  ].filter(Boolean).join(' ');

  const playBtnCls = [
    'flex items-center justify-center w-[46px] h-[46px] border-none bg-transparent cursor-pointer p-0 transition-all duration-150',
    isQuotaExhausted ? 'text-[var(--text-tertiary)] cursor-not-allowed' : 'text-[var(--accent-teal)] hover:opacity-80 hover:scale-110 active:scale-95',
    isPlayerLoading || error ? 'opacity-35 cursor-not-allowed' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rootCls}>
      {showQuota && (
        <div className={quotaBannerCls}>
          <div className="flex items-center gap-1.5">
            {isQuotaExhausted ? (
              <Lock size={13} weight="fill" className="shrink-0 opacity-70" />
            ) : (
              <span className="text-[11px] leading-none">🔊</span>
            )}
            <span className="text-xs font-medium tracking-wide">
              {isQuotaExhausted
                ? 'Audio terkunci — batas pemutaran habis'
                : maxPlays === null
                  ? 'Pemutaran tidak terbatas'
                  : `Batas pemutaran audio: ${maxPlays}×`}
            </span>
          </div>
        </div>
      )}

      {showQuota && maxPlays !== null && (
        <p className="m-0 mb-2.5 text-xs leading-snug font-semibold text-[var(--error-color)]">
          Perhatian: pemutaran audio terbatas. Dengarkan dengan saksama agar tidak kehabisan kuota.
        </p>
      )}

      <div className={`flex items-center gap-3.5 ${showQuota && maxPlays !== null ? 'pb-5 max-md:pb-[18px]' : ''}`}>
        <div className="relative w-[46px] shrink-0">
          <button
            type="button"
            className={playBtnCls}
            onClick={togglePlayPause}
            disabled={isPlayerLoading || !!error || isQuotaExhausted}
            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
          >
            {isPlayerLoading ? (
              <CircleNotch size={20} weight="bold" className="animate-spin" />
            ) : isQuotaExhausted ? (
              <Lock size={20} weight="fill" />
            ) : isPlaying ? (
              <Pause size={24} weight="fill" />
            ) : (
              <Play size={24} weight="fill" />
            )}
          </button>

          {showQuota && maxPlays !== null && (
            <div className="absolute top-[calc(100%+4px)] left-0 flex justify-start items-center gap-1.5 shrink-0">
              {Array.from({ length: maxPlays }).map((_, i) => (
                <span
                  key={i}
                  className={`inline-block w-[9px] h-[9px] max-md:w-2 max-md:h-2 rounded-full transition-all duration-200 ${
                    i >= maxPlays - playsUsed
                      ? 'bg-slate-900/15 dark:bg-[#374151] border-[1.5px] border-slate-900/30 dark:border-[#4b5563]'
                      : 'bg-[var(--accent-teal)]'
                  }`}
                />
              ))}
              <span className="text-[11px] max-md:text-[10px] font-semibold text-[var(--accent-teal)] ml-0.5 whitespace-nowrap">
                {remainingPlays} tersisa
              </span>
            </div>
          )}
        </div>

        <div
          className={`question-audio-waveform flex items-center ${fullWidth ? 'flex-1' : 'w-[55%] shrink-0'} min-w-0 h-[52px] relative rounded-md overflow-hidden focus-visible:outline-2 focus-visible:outline-[var(--accent-teal)] focus-visible:outline-offset-2 ${
            isQuotaExhausted || isDurationPending
              ? 'cursor-not-allowed opacity-70'
              : 'cursor-pointer'
          }`}
          onClick={isQuotaExhausted || isDurationPending ? undefined : handleWaveformClick}
          role="button"
          tabIndex={isQuotaExhausted || isDurationPending ? -1 : 0}
          aria-label="Seek audio"
          aria-busy={isDurationPending || undefined}
          onKeyDown={(e) => {
            if (!isQuotaExhausted && !isDurationPending && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleWaveformClick(e);
            }
          }}
        >
          <div className={`question-audio-waveform-bars flex items-center justify-between gap-0.5 w-full h-full ${isQuotaExhausted ? 'grayscale-[0.6]' : ''}`}>
            {isPlaying && displayBars
              ? displayBars.map((height, i) => {
                  const isPast = i / BAR_COUNT <= progress;
                  return (
                    <div
                      key={i}
                      className={`flex-1 min-w-[2px] max-w-1 rounded-sm will-change-[height] ${
                        isPast
                          ? 'bg-teal-400 dark:bg-[#5eead4] opacity-95'
                          : 'bg-teal-800 dark:bg-[#99f6e4] shadow-[0_0_8px_rgba(15,118,110,0.24)] dark:shadow-[0_0_8px_rgba(153,246,228,0.55)]'
                      }`}
                      style={{ height: `${Math.max(10, height * 100)}%` }}
                    />
                  );
                })
              : idleBars.map((_, i) => (
                  <div key={i} className="flex-1 min-w-[2px] max-w-1 rounded-sm h-[3px] bg-teal-300/75" />
                ))}
          </div>
        </div>

        <span className="text-[13px] font-medium text-[var(--text-secondary)] dark:text-white/70 shrink-0 tabular-nums tracking-wide min-w-[4.75rem] text-right inline-flex items-center justify-end gap-1">
          <span>{formatTime(currentTime)}</span>
          <span className="text-[var(--text-tertiary)] opacity-80">/</span>
          {isDurationPending ? (
            <CircleNotch
              size={15}
              weight="bold"
              className="animate-spin text-[var(--accent-teal)] shrink-0"
              aria-label="Memuat durasi audio"
            />
          ) : (
            <span>{formatTime(duration)}</span>
          )}
        </span>
      </div>

      {error && <p className="mt-2 text-[13px] text-[var(--error-color)]">{error}</p>}
    </div>
  );
}

export default QuestionAudioPlayer;

